import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { adminApi } from '@/api';
import {
  Alert,
  Badge,
  Card,
  CardHeader,
  ErrorState,
  LoadingState,
  ProgressBar,
} from '@/components/ui';
import { Fact, FactList } from '@/features/admin/Facts';
import { RecordHeader } from '@/features/admin/RecordHeader';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/format';

import { CustomerInvoices } from './CustomerInvoices';
import {
  formatMoney,
  formatPrice,
  statusLabel,
  statusTone,
  studentEntitlementLines,
  usageRatio,
} from './money';
import { StudentPlanActions } from './StudentPlanActions';

/**
 * One student subscription, whole, and the place it is acted on.
 *
 * Rendered by the shared subscription detail route when the link says
 * `?customer=student`. Changing a student's plan follows the student's own
 * rules: a dearer plan (or one in another currency) starts now with an
 * invoice, a cheaper one is scheduled for the period end and can be
 * cancelled; ending waits for that date. The student is notified by the
 * server.
 */
export function StudentSubscriptionDetail({ id }) {
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['admin-student-subscription', id],
    queryFn: () => adminApi.getStudentSubscription(id),
  });

  if (isPending) return <LoadingState label="Loading the subscription…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const lapsed = !data.is_running && data.status !== 'CANCELED';
  const pending = data.invoices.find(
    (invoice) => invoice.status === 'OPEN' && invoice.plan_code !== 'one-off',
  );
  // The shape the actions read, built from this record rather than fetched
  // again: the same facts, keyed on the subscription.
  const billing = {
    subscription_id: data.id,
    is_paid: data.is_running,
    cancel_at_period_end: data.cancel_at_period_end,
    scheduled_plan: data.scheduled_plan ?? null,
    plan: data.plan,
    current_period_end: data.current_period_end,
    pending_invoice: pending ?? null,
  };

  return (
    <div>
      <RecordHeader
        backTo={`${paths.admin.subscriptions}?customer=student`}
        backLabel="Subscriptions"
        eyebrow="Student subscription"
        title={data.student_name}
        subtitle={[
          data.plan_name,
          formatPrice(data.price_cents, data.currency, data.interval),
          `since ${formatDate(data.created_at)}`,
        ].join(' · ')}
        badges={
          <>
            <Badge tone={lapsed ? 'warning' : statusTone(data.status)}>
              {lapsed ? 'Lapsed' : statusLabel(data.status)}
            </Badge>
            {data.is_running && data.cancel_at_period_end && (
              <Badge tone="warning">Ends {formatDate(data.current_period_end)}</Badge>
            )}
            {data.is_running && data.scheduled_plan && (
              <Badge tone="info">
                Moves to {data.scheduled_plan.name} on {formatDate(data.current_period_end)}
              </Badge>
            )}
            {data.plan.student_entitlements?.application_boost && (
              <Badge tone="brand">Premium badge</Badge>
            )}
          </>
        }
        actions={
          <StudentPlanActions
            studentId={data.student_id}
            studentName={data.student_name}
            billing={billing}
          />
        }
        facts={[
          {
            label: 'Price',
            value: formatMoney(data.price_cents, data.currency),
            hint: data.interval === 'YEARLY' ? 'a year' : 'a month',
          },
          {
            label: data.cancel_at_period_end || !data.is_running ? 'Ends' : 'Renews',
            value: formatDate(data.current_period_end),
            hint: `Period from ${formatDate(data.current_period_start)}`,
          },
          {
            label: 'Outstanding',
            value: formatMoney(data.outstanding_cents, data.currency),
            hint: `${data.open_invoices} open invoice${data.open_invoices === 1 ? '' : 's'}`,
          },
          { label: 'Since', value: formatDate(data.created_at) },
        ]}
      />

      {lapsed && (
        <Alert tone="warning" title="This plan is not being honoured" className="mb-6">
          The paid period has run out, so the student is on Student Free. Paying the renewal
          invoice, or assigning a plan, switches it back on.
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader
              title={data.plan_name}
              description={data.plan.description ?? undefined}
              action={
                <span className="text-ink-muted text-sm">
                  {formatPrice(data.price_cents, data.currency, data.interval)}
                </span>
              }
            />
            <ul className="text-ink-muted space-y-1.5 text-sm">
              {studentEntitlementLines(data.plan.student_entitlements).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </Card>

          <Card>
            <CardHeader
              title="Usage"
              description={`This month (${data.usage_period}, UTC), against what the plan allows now.`}
            />
            <ul className="space-y-4">
              {data.usage.map((row) => {
                const ratio = usageRatio(row.used, row.limit);
                return (
                  <li key={row.key}>
                    <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
                      <span className="text-ink font-medium">{row.label}</span>
                      <span className="text-ink-muted text-xs tabular-nums">
                        {row.limit === null
                          ? `${row.used} used · unlimited`
                          : `${row.used} of ${row.limit}`}
                      </span>
                    </div>
                    {ratio === null ? (
                      <p className="text-ink-muted text-xs">No limit on this plan.</p>
                    ) : (
                      <ProgressBar value={ratio} />
                    )}
                  </li>
                );
              })}
            </ul>
          </Card>

          <CustomerInvoices studentId={data.student_id} />
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Student" />
            <Link
              to={paths.admin.studentDetail(data.student_id)}
              className="text-ink text-sm font-medium hover:underline"
            >
              {data.student_name}
            </Link>
            <p className="text-ink-muted text-sm break-all">{data.student_email}</p>
          </Card>

          <Card>
            <CardHeader title="Schedule" />
            <FactList columns={1}>
              <Fact label="Status" value={lapsed ? 'Lapsed' : statusLabel(data.status)} showEmpty />
              <Fact
                label="Period"
                value={`${formatDate(data.current_period_start)} — ${formatDate(data.current_period_end)}`}
              />
              <Fact
                label="Cancellation requested"
                value={data.cancelled_at ? formatDate(data.cancelled_at) : null}
              />
              <Fact label="Provider" value={data.provider} />
              <Fact label="Last change" value={formatDate(data.updated_at)} />
            </FactList>
          </Card>
        </div>
      </div>
    </div>
  );
}
